import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { TopBar } from "@/components/game/TopBar";
import { LeftDock } from "@/components/game/LeftDock";
import { DropFeed } from "@/components/game/DropFeed";
import { Viewport } from "@/components/game/Viewport";
import { SkillsPanel } from "@/components/game/SkillsPanel";
import { CombatPanel } from "@/components/game/CombatPanel";
import { BankPanel } from "@/components/game/BankPanel";
import { ChatPanel } from "@/components/game/ChatPanel";
import { InventoryPanel, HousingPanel } from "@/components/game/MiscPanels";
import { QuestsPanel } from "@/components/game/QuestsPanel";
import { GrandExchangePanel } from "@/components/game/GrandExchangePanel";
import { ProfilePanel } from "@/components/game/ProfilePanel";
import { MapPanel } from "@/components/game/MapPanel";
import { PetsPanel } from "@/components/game/PetsPanel";
import { MuseumPanel } from "@/components/game/MuseumPanel";
import { AchievementsPanel } from "@/components/game/AchievementsPanel";
import { LockedFeaturePanel } from "@/components/game/LockedFeaturePanel";
import { SkillPage } from "@/components/game/SkillPage";
import { FriendsPanel } from "@/components/game/FriendsPanel";
import { ClansPanel } from "@/components/game/ClansPanel";
import { EventsPanel } from "@/components/game/EventsPanel";
import { LeaderboardPanel } from "@/components/game/LeaderboardPanel";
import { EquipmentPanel } from "@/components/game/EquipmentPanel";
import { DungeonsPanel } from "@/components/game/DungeonsPanel";
import { WorldBossesPanel } from "@/components/game/WorldBossesPanel";
import { CountryPanel } from "@/components/game/CountryPanel";
import { AdminPanel } from "@/components/game/AdminPanel";
import { GeneralStorePanel } from "@/components/game/GeneralStorePanel";
import { ArenaPanel } from "@/components/game/ArenaPanel";
import { WildernessPanel } from "@/components/game/WildernessPanel";
import { HomeCountryPicker } from "@/components/game/HomeCountryPicker";
import { PlayerCard } from "@/components/game/PlayerCard";
import { ChatBubble } from "@/components/game/ChatBubble";
import { useGame } from "@/context/GameContext";

const PANELS = {
  profile: ProfilePanel,
  inventory: InventoryPanel,
  equipment: EquipmentPanel,
  bank: BankPanel,
  map: MapPanel,
  skills: SkillsPanel,
  combat: CombatPanel,
  exchange: GrandExchangePanel,
  pets: PetsPanel,
  museum: MuseumPanel,
  achievements: AchievementsPanel,
  chat: ChatPanel,
  quests: QuestsPanel,
  housing: HousingPanel,
  dungeons: DungeonsPanel,
  worldbosses: WorldBossesPanel,
  country: CountryPanel,
  general_store: GeneralStorePanel,
  arena: ArenaPanel,
  wilderness: WildernessPanel,
  friends: FriendsPanel,
  clans: ClansPanel,
  events: EventsPanel,
  leaderboard: LeaderboardPanel,
  admin: AdminPanel,
};

const LOCKED = { party: 50, leagues: 150 };

export default function Game() {
  const [searchParams, setSearchParams] = useSearchParams();
  const panel = searchParams.get("panel") || "profile";
  const focus = searchParams.get("focus") || null;
  const skillParam = searchParams.get("skill") || null;
  const { snap, refresh } = useGame();

  // First-run: block the UI until the player picks a homeland. We use
  // `snap` truthiness to avoid flashing the picker before state loads.
  const needsHomePick = snap && !snap.home_country_iso;

  async function onHomePicked(country) {
    await refresh?.();
    // Take the player straight to the Map focused on their new homeland.
    const next = new URLSearchParams();
    next.set("panel", "map");
    next.set("iso", country.iso_a3);
    setSearchParams(next, { replace: true });
  }

  // Local key that changes whenever focus is requested (even to same skill again)
  // so SkillsPanel can re-trigger its highlight effect.
  const [focusKey, setFocusKey] = useState(0);
  useEffect(() => {
    if (focus) setFocusKey((k) => k + 1);
  }, [focus, panel]);

  const handleChange = (nextPanel, opts = {}) => {
    const next = new URLSearchParams();
    next.set("panel", nextPanel);
    if (opts.focus) next.set("focus", opts.focus);
    if (opts.skill) next.set("skill", opts.skill);
    setSearchParams(next, { replace: false });
  };

  const goBackToSkills = () => handleChange("skills");

  const totalLevel = useMemo(() => {
    if (!snap?.skills) return 0;
    return Object.values(snap.skills).reduce((s, v) => s + (v?.level ?? 0), 0);
  }, [snap?.skills]);

  const isLocked = panel in LOCKED;
  const isSkillPage = panel === "skill" && !!skillParam;
  const Panel = isLocked || isSkillPage ? null : (PANELS[panel] || ProfilePanel);
  const extraProps = {
    ...(panel === "skills" ? { focusSkill: focus, focusKey } : {}),
    ...(panel === "combat" ? { focusSkill: focus, focusKey } : {}),
  };

  const showViewport = panel === "combat" || isSkillPage;

  return (
    <div className="h-screen flex flex-col bg-[color:var(--bg-void)]" data-testid="game-shell">
      {needsHomePick && <HomeCountryPicker onPicked={onHomePicked} />}
      <PlayerCard />
      <ChatBubble />
      <TopBar />
      <div className="flex-1 flex overflow-hidden">
        <LeftDock current={panel} currentSkill={skillParam} onChange={handleChange} totalLevel={totalLevel} skills={snap?.skills} isAdmin={!!snap?.is_admin} />

        <main className="flex-1 flex flex-col overflow-hidden">
          {showViewport && (
            <div className="px-4 pt-4">
              <Viewport />
            </div>
          )}
          <div className="flex-1 overflow-y-auto scroll-thin" data-testid="content-scroll">
            {isLocked ? (
              <LockedFeaturePanel feature={panel} totalLevel={totalLevel} />
            ) : isSkillPage ? (
              <SkillPage skillName={skillParam} onBack={goBackToSkills} />
            ) : (
              <Panel {...extraProps} />
            )}
          </div>
        </main>
      </div>
      <DropFeed />
    </div>
  );
}
